import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Mail, Phone } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'January 4, 2026';
  
  return (
    <div className="min-h-screen bg-ivory-50" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="relative gradient-hero py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center">
              <Shield className="w-7 h-7 text-gold-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">Privacy Policy</h1>
          </div>
          <p className="text-ivory-500 font-body">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-12 p-8 rounded-xl bg-ivory-100 border border-ivory-300/50 shadow-luxury">
              <p className="text-ivory-600 font-body leading-relaxed">
                Engraveca (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase. Please read this policy carefully. By using our services, you consent to the data practices described in this statement.
              </p>
            </section>

            {/* Personal Information */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-gold-600" />
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 m-0">Personal Information We Collect</h2>
              </div>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">
                  &quot;Personal information&quot; refers to data that identifies or describes an individual, including but not limited to:
                </p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Name, email address, phone number, and mailing address
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Billing and payment information (credit card numbers, billing address)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Company name and business information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Order history and product preferences
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Communications you send to us (emails, chat messages, phone calls)
                  </li>
                </ul>
              </div>
            </section>

            {/* Automatically Collected Information */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-gold-600" />
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 m-0">Automatically Collected Information</h2>
              </div>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">
                  When you visit our website, we may automatically collect certain information about your device and browsing activity:
                </p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    IP address and domain name
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Browser type and operating system
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Date and time of your visit
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Pages viewed and links clicked
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Referring website or source
                  </li>
                </ul>
                <p className="text-ivory-500 font-body mt-4 text-sm">
                  This information helps us analyze website traffic and improve your browsing experience. It does not personally identify you.
                </p>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">How We Use Your Information</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">We use the information we collect to:</p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Process and fulfill your orders
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Send order confirmations, shipping updates, and invoices
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Respond to your inquiries and provide customer support
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Send promotional communications (with your consent)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Improve our website, products, and services
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Comply with legal obligations
                  </li>
                </ul>
              </div>
            </section>

            {/* Children's Privacy */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Children&apos;s Privacy</h2>
              <div className="p-6 rounded-xl bg-gold-500/10 border border-gold-500/20">
                <p className="text-ivory-700 font-body">
                  Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from minors. If you are under 18, please do not submit any personal information through our website. If you believe a minor has provided us with personal information, please contact us immediately so we can delete such information.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Cookies and Tracking Technologies</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">
                  Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us:
                </p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Remember your preferences and settings
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Keep items in your shopping cart
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Analyze website traffic and usage patterns
                  </li>
                </ul>
                <p className="text-ivory-500 font-body mt-4 text-sm">
                  You can control cookies through your browser settings. However, disabling cookies may affect the functionality of certain features on our website.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-gold-600" />
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 m-0">Data Security</h2>
              </div>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, firewalls, and regular security assessments. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Information Sharing */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Information Sharing and Disclosure</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    <strong>Service Providers:</strong> With trusted third parties who assist us in operating our website, processing payments, or fulfilling orders
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    <strong>Legal Requirements:</strong> When required by law or to respond to legal process
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    <strong>Protection:</strong> To protect our rights, property, or safety, or that of our users or the public
                  </li>
                </ul>
              </div>
            </section>

            {/* Third-Party Links */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Third-Party Links</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Your Rights</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body mb-4">Depending on your location, you may have the right to:</p>
                <ul className="space-y-2 text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Access the personal information we hold about you
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Request correction of inaccurate information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Request deletion of your personal information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold-600 mt-1">•</span>
                    Opt out of marketing communications
                  </li>
                </ul>
                <p className="text-ivory-500 font-body mt-4 text-sm">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </div>
            </section>

            {/* Policy Changes */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Changes to This Policy</h2>
              <div className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50">
                <p className="text-ivory-600 font-body">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new policy on this page with an updated &quot;Last Updated&quot; date. We encourage you to review this policy periodically.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">Contact Us</h2>
              <div className="p-6 rounded-xl gradient-champagne border border-gold-500/20">
                <p className="text-ivory-700 font-body mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gold-600" />
                    <a href="mailto:privacy@engraveca.com" className="text-gold-700 hover:text-gold-800 font-body">
                      privacy@engraveca.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gold-600" />
                    <a href="tel:+18005210527" className="text-gold-700 hover:text-gold-800 font-body">
                      +1 800 521 0527
                    </a>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gold-500/20">
                  <p className="text-ivory-600 font-body text-sm">
                    <strong>Engraveca</strong><br />
                    1489 East Warner Avenue<br />
                    Santa Ana, CA 92705
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
